import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

const PROBE_URL = '/favicon.ico'
const PROBE_TIMEOUT_MS = 3000
const FAIL_THRESHOLD = 3

async function probeOnline(): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
    const res = await fetch(PROBE_URL, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    })
    clearTimeout(timer)
    return res.ok
  } catch {
    return false
  }
}

export function useOfflineDetection(): boolean {
  const [offline, setOffline] = useState(() => !navigator.onLine)
  const failsRef = useRef(0)

  const handleOnline = useCallback(() => {
    failsRef.current = 0
    setOffline(false)
  }, [])

  const handleOffline = useCallback(() => {
    setOffline(true)
  }, [])

  useEffect(() => {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [handleOnline, handleOffline])

  useEffect(() => {
    let cancelled = false
    const interval = setInterval(async () => {
      if (cancelled) return
      const ok = await probeOnline()
      if (cancelled) return
      if (!ok) {
        failsRef.current += 1
        if (failsRef.current >= FAIL_THRESHOLD) {
          setOffline(true)
        }
      } else {
        failsRef.current = 0
        setOffline(false)
      }
    }, 5000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  return offline
}

export interface OfflineBannerProps {
  label?: string
  onRetry?: () => void
}

export function OfflineBanner({
  label,
  onRetry,
}: OfflineBannerProps) {
  const { t } = useTranslation()
  const offline = useOfflineDetection()
  const displayLabel = label ?? t('common.offlineRetry')

  if (!offline) return null

  return (
    <div className="offline" role="status" aria-live="polite">
      <span>{displayLabel}</span>
      {onRetry != null && (
        <button type="button" onClick={onRetry}>
          {displayLabel}
        </button>
      )}
    </div>
  )
}
